// External
import React, { useMemo, useState, useCallback } from "react";
import classNames from "classnames";
import _ from "lodash";
import {
  Button,
  DropdownMenu,
  Modal,
  SideNavigation,
  SplitButtonDropdown,
} from "@toddle-edu/ds-web";

// Internal
import FullScreenLoader from "UIComponents/FullScreenLoader";
import DialogueBoxV3 from "UIComponents/DialogueBoxV3";

// Relative
import { useAcademicRecordsModal } from "../../../../context";
import DefaultSettingsContent from "../DefaultSettingsContent";
import GeneralGradingPeriodsSection from "../GeneralGradingPeriodsSection";
import { useDisplaySettingsModalData } from "./hooks";
import { APPLY_OPTION_KEYS } from "../../modules/constants";

// Styles
import classes from "./DisplaySettingsModal.scss";
import { ReloadArrowOutlined } from "@toddle-edu/ds-icons";

const TEST_ID_PREFIX = "transcriptsV3-academicRecords-displaySettingsModal";

const modalHeaderStyle = { marginBottom: 0 };
const modalBodyStyle = {
  padding: 0,
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
  overflow: "hidden",
};

const CustomSideNavigation = ({ options, ...props }) => {
  const processedOptions = useMemo(() => {
    return _.map(options, option => {
      const { titleElement, ...rest } = option;
      return {
        ...rest,
        title: titleElement !== undefined ? titleElement : option.title,
      };
    });
  }, [options]);

  return <SideNavigation options={processedOptions} {...props} />;
};

const DisplaySettingsModal = ({ academicRecordsQueryRef, currentStudent }) => {
  const headingClass = classNames(classes.heading);
  const subHeadingClass = classNames("text-body", classes.subHeading);
  const { openModal, modalInfo } = useAcademicRecordsModal();
  const fromMultipleStudents = _.get(
    modalInfo,
    "metadata.fromMultipleStudents",
    false
  );
  const [selectedOption, setSelectedOption] = useState(
    fromMultipleStudents
      ? APPLY_OPTION_KEYS.APPLY_FOR_MULTIPLE_STUDENTS
      : APPLY_OPTION_KEYS.APPLY_FOR_THIS_STUDENT
  );
  const [
    showResetConfirmationDialog,
    setShowResetConfirmationDialog,
  ] = useState(false);
  const [contentResetVersion, setContentResetVersion] = useState(0);
  const {
    closeModal,
    defaultSettingsContentProps,
    gradingPeriodsSectionProps,
    isGradingPeriodsSettingsMode,
    handleApplyForStudent,
    handleApplyForMultipleStudents,
    validateGradingPeriods,
    handleResetToDefault,
    handleSideNavigationClick,
    heading,
    isResetDisabled,
    navOptions,
    paragraph,
    selectedDraftKey,
    selectedGradeId,
    selectedTab,
    shouldShowModalLoader,
    currentStudent: selectedStudent,
    t,
  } = useDisplaySettingsModalData({
    academicRecordsQueryRef,
    currentStudent,
    overriddenTitleClassName: classes.overriddenTitle,
    overriddenDotClassName: classes.overriddenDot,
  });

  const handleResetClick = useCallback(() => {
    setShowResetConfirmationDialog(true);
  }, []);

  const handleResetConfirmationClose = useCallback(() => {
    setShowResetConfirmationDialog(false);
  }, []);

  const handleResetConfirm = useCallback(() => {
    handleResetToDefault({
      onCompleted: () => {
        setContentResetVersion(prev => prev + 1);
      },
    });
  }, [handleResetToDefault]);

  const handleApplyByOption = useCallback(
    async optionKey => {
      if (optionKey === APPLY_OPTION_KEYS.APPLY_FOR_THIS_STUDENT) {
        handleApplyForStudent();
      } else if (optionKey === APPLY_OPTION_KEYS.APPLY_FOR_MULTIPLE_STUDENTS) {
        const isValid = await validateGradingPeriods();
        if (!isValid) return;
        openModal("APPLY_TO_MULTIPLE_STUDENTS", {
          currentStudent: selectedStudent,
          onApply: handleApplyForMultipleStudents,
        });
      }
    },
    [
      handleApplyForStudent,
      validateGradingPeriods,
      openModal,
      selectedStudent,
      handleApplyForMultipleStudents,
    ]
  );

  const handleDropdownOptionClick = useCallback(
    ({ key }) => {
      setSelectedOption(key);
      handleApplyByOption(key);
    },
    [handleApplyByOption]
  );

  const handleApplyClick = useCallback(
    () => handleApplyByOption(selectedOption),
    [handleApplyByOption, selectedOption]
  );

  return (
    <Modal width={1200} height={750} onClose={closeModal}>
      <Modal.Header
        style={modalHeaderStyle}
        title={t("common:display_settings")}
        onClose={closeModal}
        hasSeparator={true}
        testId={TEST_ID_PREFIX}
      ></Modal.Header>
      <Modal.Body isScrollable={false} style={modalBodyStyle}>
        <div className={classes.modalBodyContainer}>
          <div className={classes.sideNavContainer}>
            <CustomSideNavigation
              options={navOptions}
              selectedOptionKey={selectedTab}
              onClick={handleSideNavigationClick}
              theme="subtle"
            />
          </div>
          <div className={classes.contentWrapper}>
            <div className={classes.headingContainer}>
              <div className={classes.headingTextContainer}>
                <h6 className={headingClass}>{heading}</h6>
                <p className={subHeadingClass}>{paragraph}</p>
              </div>
              {selectedGradeId ? (
                <div className={classes.resetButtonContainer}>
                  <Button
                    type="outlined"
                    variant="neutral"
                    icon={<ReloadArrowOutlined />}
                    onClick={handleResetClick}
                    disabled={isResetDisabled}
                    testId={`${TEST_ID_PREFIX}-reset-to-default`}
                  >
                    {t("common:reset_to_default")}
                  </Button>
                </div>
              ) : null}
            </div>
            <div className={classes.listContainer}>
              {isGradingPeriodsSettingsMode ? (
                <GeneralGradingPeriodsSection
                  key="grading-periods-section"
                  {...gradingPeriodsSectionProps}
                />
              ) : (
                <DefaultSettingsContent
                  key={`${selectedDraftKey}-${selectedTab}-${contentResetVersion}`}
                  {...defaultSettingsContentProps}
                />
              )}
            </div>
            {shouldShowModalLoader ? <FullScreenLoader /> : null}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer hasSeparator={true}>
        <div className={classes.footerActions}>
          <Button
            onClick={closeModal}
            type="outlined"
            variant="neutral"
            testId={`${TEST_ID_PREFIX}-close`}
          >
            {t("common:close")}
          </Button>
          <SplitButtonDropdown
            overlay={
              <DropdownMenu
                value={selectedOption}
                showSelection={true}
                options={[
                  {
                    key: APPLY_OPTION_KEYS.APPLY_FOR_THIS_STUDENT,
                    label: t("transcript:apply_for_this_student"),
                  },
                  {
                    key: APPLY_OPTION_KEYS.APPLY_FOR_MULTIPLE_STUDENTS,
                    label: t("transcript:apply_for_multiple_students"),
                  },
                ]}
                onClick={handleDropdownOptionClick}
                testId={`${TEST_ID_PREFIX}-dropdown-menu`}
              />
            }
          >
            <Button
              variant="progressive"
              onClick={handleApplyClick}
              testId={`${TEST_ID_PREFIX}-apply`}
            >
              {selectedOption === APPLY_OPTION_KEYS.APPLY_FOR_THIS_STUDENT
                ? t("transcript:apply_for_this_student")
                : t("transcript:apply_for_multiple_students")}
            </Button>
          </SplitButtonDropdown>
        </div>
      </Modal.Footer>
      {showResetConfirmationDialog ? (
        <DialogueBoxV3
          modalTitle={t("transcript:reset_default_configuration_heading")}
          modalBody={t(
            "transcript:reset_default_configuration_confirmation_text"
          )}
          button1={t("common:cancel")}
          button2={t("common:reset_to_default")}
          button2Props={{
            variant: "destructive",
            "data-test-id": `${TEST_ID_PREFIX}-reset-confirm-button`,
          }}
          button1Props={{
            variant: "neutral",
            type: "outlined",
            "data-test-id": `${TEST_ID_PREFIX}-reset-cancel-button`,
          }}
          onClickButton2={handleResetConfirm}
          toggleDialogueBoxDisplay={handleResetConfirmationClose}
        />
      ) : null}
    </Modal>
  );
};

export default DisplaySettingsModal;
